import { connect } from 'react-redux';
import { get } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useRef, useEffect, useState } from 'react';

import { loading, loadingSuccess } from '@actions/loading';

import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import { Dropdown } from '@commons/utils/styledLibraryComponents';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getUserTimezone } from '@commons/utils/date';
import { ListView } from '@commons/utils/styledLibraryComponents';
import { PeriodDatePicker } from '@commons/DatePickers/PeriodDatePicker';
import EmptyState from '@commons/EmptyState';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';

import { ENUM_QUERY_PARAMS, useListViewQueryParams } from '@hooks/useListViewQueryParams';
import { usePeriodDatePickerState } from '@hooks/usePeriodDatePickerState';

import { getClientInfo } from '@selectors/client';
import { getSalesPointStores } from '@selectors/stores';

import { taskService } from '@services/task';

import { Container, ListViewContainer, FilterContainer } from './styledComponents';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import utilsActions from './utils/actions';
import utilsColumns from './utils/columns';

export const BackOfficeTasks = (props) => {
  const {
    user,
    stores,
    client: { storeName },
    match,
    showErrorMessage,
    showSuccessMessage,
  } = props;
  const path = get(match, 'path');
  const translatedStoresName = getClientStoreNameTranslation(storeName, true).toLowerCase();
  const userLanguageCode = get(user, 'lnkLanguageAccountrel.code', 'fr');
  const listViewRef = useRef();

  const STATUS_MAP = {
    SUCCESS: 'SUCCESS',
    FAILURE: 'FAILURE',
    RETRY: 'FAILURE',
    REVOKED: 'STOPPED',
    PENDING: 'PENDING',
    STARTED: 'PENDING',
  };

  const DEFAULT_COLUMNS = utilsColumns.getColumnsSettings(translatedStoresName);

  const CHOICES_DROPDOWN_TASK_NAME = [
    {
      id: 0,
      name: i18next.t('BACKOFFICE.TASKS.NAME.CASHIER_PRODUCT_SALE_SYNC'),
      itemValue: 'cashier_product_sale_sync',
    },
    {
      id: 1,
      name: i18next.t('BACKOFFICE.TASKS.NAME.CASHIER_STORE_SYNC', {
        storeNamePlural: translatedStoresName,
      }),
      itemValue: 'cashier_store_sync',
    },
    {
      id: 2,
      name: i18next.t('BACKOFFICE.TASKS.NAME.PRODUCT_SYNC'),
      itemValue: 'product_sync',
    },
    {
      id: 3,
      name: i18next.t('BACKOFFICE.TASKS.NAME.PRE_COMPUTE_PRODUCTION'),
      itemValue: 'pre_compute_production',
    },
    {
      id: 4,
      name: i18next.t('BACKOFFICE.TASKS.NAME.PRE_COMPUTE_ORDER'),
      itemValue: 'pre_compute_order',
    },
  ];

  const USER_TIMEZONE = getUserTimezone();
  const DEFAULT_START_DATE = moment.tz(USER_TIMEZONE).subtract(1, 'month');
  const DEFAULT_END_DATE = moment.tz(USER_TIMEZONE);

  const periodPickerState = usePeriodDatePickerState(
    DEFAULT_START_DATE,
    DEFAULT_END_DATE,
    USER_TIMEZONE,
  );

  const [columns] = useState(DEFAULT_COLUMNS);
  const [maxFutureDate, setMaxFutureDate] = useState(null);
  const [selectedTaskNames, setSelectedTaskNames] = useState(CHOICES_DROPDOWN_TASK_NAME);
  const [displayedTasks, setDisplayedTasks] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [listViewQueryParams, setListViewQueryParams] = useListViewQueryParams();

  // Max selectable period is 1 months
  useEffect(() => {
    const maxEndDate = moment.tz(periodPickerState.startDate, USER_TIMEZONE).add(1, 'month');

    setMaxFutureDate(moment.min(DEFAULT_END_DATE, maxEndDate));
  }, [periodPickerState.startDate]);

  /*************************/
  /** Fetch list of tasks **/
  /*************************/

  const formatParams = (params) => {
    const formattedParams = [];
    if ('date' in params) {
      formattedParams.push(moment(params.date).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR));
    }
    if ('start_date' in params && 'end_date' in params) {
      formattedParams.push(
        moment(params.start_date).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR) +
          ' - ' +
          moment(params.end_date).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
      );
    }
    if ('store_id' in params) {
      const filtered_store = stores.find(({ id }) => id === params.store_id);
      if (filtered_store) {
        formattedParams.push(filtered_store.name);
      }
    }
    if ('store_ids' in params) {
      const storeIdsSet = new Set(params.store_ids);
      const filtered_stores = stores.filter(({ id }) => storeIdsSet.has(id));

      filtered_stores.forEach(({ name }) => {
        formattedParams.push(name);
      });
    }

    return formattedParams;
  };

  const handleGetTasks = async () => {
    try {
      const { results } = await taskService.getTasks(
        selectedTaskNames.map((task) => task.itemValue),
        periodPickerState.startDate.clone().startOf('day'),
        periodPickerState.endDate.clone().endOf('day'),
      );

      const formattedTasks = results.map((task) => ({
        ...task,
        formattedParams: formatParams(task.params),
        formattedStatus: { status: STATUS_MAP[task.status], progress: task.progress },
      }));

      setDisplayedTasks(formattedTasks);
    } catch {
      showErrorMessage(i18next.t('BACKOFFICE.TASKS.FETCHING_ERROR'));

      setDisplayedTasks([]);
    }
  };

  useEffect(() => {
    if (
      !selectedTaskNames.length ||
      !periodPickerState.startDate ||
      !periodPickerState.endDate ||
      !user
    ) {
      return;
    }
    setIsLoading(true);

    (async () => {
      await handleGetTasks();

      setIsLoading(false);
    })();
  }, [periodPickerState.startDate, periodPickerState.endDate, selectedTaskNames]);

  /*************/
  /** Actions **/
  /*************/

  const handleStopTask = async (taskId) => {
    try {
      await taskService.stopTask(taskId);

      const formattedTasks = displayedTasks.map((task) =>
        task.id === taskId
          ? { ...task, formattedStatus: { status: STATUS_MAP.REVOKED, progress: 0 } }
          : task,
      );

      setDisplayedTasks(formattedTasks);

      showSuccessMessage(i18next.t('BACKOFFICE.TASKS.STOP_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('BACKOFFICE.TASKS.STOP_FAILURE'));
    }
  };

  const defaultRowActions = utilsActions.getRowActionsSettings({
    stopTask: handleStopTask,
  });

  const [rowActions, setRowActions] = useState(defaultRowActions);

  useEffect(() => {
    setRowActions(
      utilsActions.getRowActionsSettings({
        stopTask: handleStopTask,
      }),
    );
  }, [displayedTasks]);

  return (
    <Container>
      <NavigationBreadCrumb featurePath={path} />

      <ListViewContainer>
        <FilterContainer>
          <Dropdown
            iconSrc="/images/inpulse/pin-black-small.svg"
            isDisabled={isLoading}
            isUniqueSelection={false}
            items={CHOICES_DROPDOWN_TASK_NAME}
            placeholder={i18next.t('BACKOFFICE.TASKS')}
            searchPlaceholder={i18next.t('GENERAL.SEARCH')}
            selectedItems={selectedTaskNames}
            style={{ width: '360px' }}
            isRequired
            onSelectionChange={setSelectedTaskNames}
          />
          <PeriodDatePicker
            disabled={isLoading}
            endDate={periodPickerState.endDate}
            focusedDateInput={periodPickerState.focusedDateInput}
            maxFutureDate={
              !!periodPickerState.startDate && !!periodPickerState.endDate
                ? DEFAULT_END_DATE
                : maxFutureDate
            }
            setFocusedDateInput={periodPickerState.setFocusedDateInput}
            startDate={periodPickerState.startDate}
            timezone={USER_TIMEZONE}
            onDatesChange={periodPickerState.handleSelectedDates}
          />
        </FilterContainer>
        <ListView
          columns={columns}
          data={displayedTasks}
          defaultCurrentPage={listViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE]}
          defaultMaxPerPage={listViewQueryParams[ENUM_QUERY_PARAMS.MAX_PER_PAGE]}
          defaultOrderBy={'date'}
          defaultOrderType={'desc'}
          handleCurrentPageChange={(input) =>
            setListViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE](input)
          }
          handleMaxPerPageChange={(input) =>
            setListViewQueryParams[ENUM_QUERY_PARAMS.MAX_PER_PAGE](input)
          }
          handleOrderByChange={(input) => setListViewQueryParams[ENUM_QUERY_PARAMS.ORDER_BY](input)}
          handleOrderTypeChange={(input) =>
            setListViewQueryParams[ENUM_QUERY_PARAMS.ORDER_TYPE](input)
          }
          isLoading={isLoading}
          languageCode={userLanguageCode}
          minActionsInActionsDropdown={1}
          padding={'24px 24px 0px 24px'}
          ref={listViewRef}
          renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
          rowActions={rowActions}
          hideSearchbar
        />
      </ListViewContainer>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  stores: getSalesPointStores(state.baseReducer.activeStores),
  client: getClientInfo(state.baseReducer.user),
});

const mapDispatchToProps = (dispatch) => ({
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(BackOfficeTasks);
